import {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { StackHolder, UserType } from "../components/Enums";

interface AuthContextType {
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  permissions: string[];
  user: any;
  login: (
    token: string,
    userPermissions: string[],
    user: object,
    companyLogo: string
  ) => void;
  loginSuperAdmin: (token: string, user: object) => void;
  logout: () => void;
  logoutSuperAdmin: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [user, setUser] = useState<any>(null);

  // Check authentication status on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    const superAdminToken = localStorage.getItem("superAdminToken");

    if (superAdminToken) {
      const superAdminUser = JSON.parse(
        localStorage.getItem("superAdminUser") || "{}"
      );
      setIsSuperAdmin(true);
      setUser(superAdminUser);
    } else if (token) {
      const storedPermissions = JSON.parse(
        localStorage.getItem("permissions") || "[]"
      );
      const storedUser = JSON.parse(localStorage.getItem("me") || "{}");
      setIsAuthenticated(true);
      setPermissions(storedPermissions);
      setUser(storedUser);
    }
  }, []);

  // Regular user login
  const login = (
    token: string,
    userPermissions: string[],
    userObj: object,
    companyLogo: string
  ) => {
    localStorage.setItem("token", token);
    localStorage.setItem("permissions", JSON.stringify(userPermissions));
    localStorage.setItem("me", JSON.stringify(userObj));
    localStorage.setItem("companyLogo", companyLogo);
    setIsAuthenticated(true);
    setPermissions(userPermissions);
    setUser(userObj);
    setIsSuperAdmin(false);
  };

  // Super Admin login
  const loginSuperAdmin = (token: string, userObj: object) => {
    localStorage.setItem("superAdminToken", token);
    localStorage.setItem("superAdminUser", JSON.stringify(userObj));
    setIsSuperAdmin(true);
    setUser(userObj);
    setIsAuthenticated(false);
    setPermissions([]);
  };

  // Regular user logout
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("permissions");
    localStorage.removeItem("me");
    localStorage.removeItem("settings");
    localStorage.removeItem("companyLogo");
    setIsAuthenticated(false);
    setPermissions([]);
    setUser(null);
  };

  // Super Admin logout
  const logoutSuperAdmin = () => {
    localStorage.removeItem("superAdminToken");
    localStorage.removeItem("superAdminUser");
    setIsSuperAdmin(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isSuperAdmin,
        permissions,
        user,
        login,
        loginSuperAdmin,
        logout,
        logoutSuperAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const getToken = () => {
  return localStorage.getItem("token") || "";
};

export const getAdminToken = () => {
  return localStorage.getItem("superAdminToken") || "";
};

export const getMe = () => {
  const userDetails = localStorage.getItem("me");
  if (userDetails) {
    return JSON.parse(userDetails);
  } else {
    return {};
  }
};

export const getAdminUser = () => {
  const userDetails = localStorage.getItem("superAdminUser");
  if (userDetails) {
    return JSON.parse(userDetails);
  } else {
    return {};
  }
};

export const getPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  if (permissions) {
    return JSON.parse(permissions);
  } else {
    return [];
  }
};

export const getLoginUserType = () => {
  const user = getMe();
  const stackHolderType =
    user?.user_type === UserType.STACK_HOLDER ? user?.stack_holder_type : null;

  return {
    isCompanyAdmin: user?.user_type === UserType.ADMIN && user?.is_admin,
    isAdmin: user?.user_type === UserType.ADMIN,
    isSupplier: stackHolderType === StackHolder.SUPPLIER,
    isBuyer: stackHolderType === StackHolder.BUYER,
    isBroker: stackHolderType === StackHolder.BROKER,
    isCHA: stackHolderType === StackHolder.CHA,
    isForwarder: stackHolderType === StackHolder.FORWARDER,
  };
};

export const getSettingsData = () => {
  const settings = localStorage.getItem("settings");
  if (settings) {
    return JSON.parse(settings);
  } else {
    return {};
  }
};
